const DEFAULT_REACT_ENTRY = `import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import './index.css';

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
`;

const DEFAULT_REACT_STYLES = `:root {
  color-scheme: light;
  font-family: Tahoma, Arial, sans-serif;
}

* {
  box-sizing: border-box;
}

html,
body,
#root {
  margin: 0;
  min-height: 100%;
}

body {
  background: #f5f5f7;
}
`;

const escapeTemplateLiteral = (value) =>
  String(value || '')
    .replace(/\\/g, '\\\\')
    .replace(/`/g, '\\`')
    .replace(/\$\{/g, '\\${');

const sanitizeFiles = (files) => {
  if (!files || typeof files !== 'object' || Array.isArray(files)) {
    return null;
  }

  const entries = Object.entries(files).filter(
    ([filePath, content]) => typeof filePath === 'string' && typeof content === 'string'
  );

  if (!entries.length) {
    return null;
  }

  return Object.fromEntries(entries);
};

export const hasPreviewFiles = (files) => Boolean(sanitizeFiles(files));

const getValueFromCandidates = (source, candidates) => {
  if (!source || typeof source !== 'object') {
    return null;
  }

  return candidates.find((key) => typeof source[key] === 'string' && source[key].trim()) || null;
};

export const buildHtmlPreviewApp = (html, language = 'en') => {
  const direction = language === 'fa' ? 'rtl' : 'ltr';

  return `import React from 'react';

export default function App() {
  return (
    <main lang="${language}" dir="${direction}" dangerouslySetInnerHTML={{ __html: \`${escapeTemplateLiteral(html)}\` }} />
  );
}
`;
};

export const buildPreviewFiles = ({ files, html, language = 'en' }) => {
  const normalizedFiles = sanitizeFiles(files);

  if (normalizedFiles) {
    return {
      'src/App.jsx': normalizedFiles['src/App.jsx'] || normalizedFiles['src/App.tsx'] || buildHtmlPreviewApp(html || '', language),
      'src/main.jsx': normalizedFiles['src/main.jsx'] || normalizedFiles['src/main.tsx'] || DEFAULT_REACT_ENTRY,
      'src/index.css': normalizedFiles['src/index.css'] || DEFAULT_REACT_STYLES,
      ...normalizedFiles
    };
  }

  if (!html?.trim()) {
    return null;
  }

  return {
    'src/App.jsx': buildHtmlPreviewApp(html, language),
    'src/main.jsx': DEFAULT_REACT_ENTRY,
    'src/index.css': DEFAULT_REACT_STYLES
  };
};

export const extractPreviewFiles = (result) => {
  if (!result || typeof result !== 'object') {
    return null;
  }

  return sanitizeFiles(result.files) || sanitizeFiles(result?.sandbox?.files);
};

export const extractSandboxSessionId = (source) => {
  if (!source || typeof source !== 'object') {
    return null;
  }

  const directKey = getValueFromCandidates(source, [
    'sessionId',
    'SessionId',
    'sandboxSessionId',
    'SandboxSessionId',
    'previewSessionId',
    'PreviewSessionId'
  ]);

  if (directKey) {
    return source[directKey];
  }

  const sandboxKey = getValueFromCandidates(source.sandbox, [
    'sessionId',
    'SessionId',
    'sandboxSessionId',
    'SandboxSessionId'
  ]);

  return sandboxKey ? source.sandbox[sandboxKey] : null;
};

export const createPreviewSourceKey = ({ files, html, language = 'en' }) =>
  JSON.stringify({
    language,
    html: html || '',
    files: files || null
  });
